import { useMemo, useState } from "react";
import "../../styles/design-system.css";
import "../features.css";
import { Badge } from "../../components/ui/Badge";
import { Tag } from "../../components/ui/Tag";
import { track } from "../../utils/analytics";
import { useFeatureView } from "../../utils/useFeatureView";

const SUPPRESS_N = 5; // suppress cells with n < SUPPRESS_N to reduce re-identification risk

const TRIALS = [
  {
    id: "CARDIO-Equity-Ph2",
    name: "CARDIO-Equity Phase II",
    target: 200,
    enrolled: 142,
    thresholds: { black: 0.13, hispanic: 0.19, asian: 0.06, aian: 0.01 },
    cohort: {
      race: { white: 78, black: 32, asian: 8, aian: 4, nhpi: 1, multi: 11, unknown: 8 },
      ethnicity: { hispanic: 24, nonHispanic: 110, unknown: 8 },
      sex: { female: 79, male: 62, other: 1 },
      ageBands: { "18-44": 22, "45-64": 78, "65+": 42 },
      urbanicity: { urban: 88, suburban: 38, rural: 16 },
    },
  },
  {
    id: "FOOD-Rx-Pilot",
    name: "FOOD-Rx Pilot",
    target: 80,
    enrolled: 62,
    thresholds: { black: 0.20, hispanic: 0.30, asian: 0.04, aian: 0.01 },
    cohort: {
      race: { white: 18, black: 22, asian: 4, aian: 2, nhpi: 0, multi: 6, unknown: 10 },
      ethnicity: { hispanic: 26, nonHispanic: 30, unknown: 6 },
      sex: { female: 41, male: 21, other: 0 },
      ageBands: { "18-44": 18, "45-64": 30, "65+": 14 },
      urbanicity: { urban: 48, suburban: 10, rural: 4 },
    },
  },
  {
    id: "HTN-Community",
    name: "HTN-Community Adapt",
    target: 120,
    enrolled: 88,
    thresholds: { black: 0.22, hispanic: 0.18, asian: 0.05, aian: 0.01 },
    cohort: {
      race: { white: 38, black: 24, asian: 6, aian: 1, nhpi: 0, multi: 9, unknown: 10 },
      ethnicity: { hispanic: 16, nonHispanic: 64, unknown: 8 },
      sex: { female: 47, male: 40, other: 1 },
      ageBands: { "18-44": 12, "45-64": 48, "65+": 28 },
      urbanicity: { urban: 56, suburban: 22, rural: 10 },
    },
  },
];

const RACE_LABEL = {
  white: "White",
  black: "Black or African American",
  asian: "Asian",
  aian: "AI / AN",
  nhpi: "NH / PI",
  multi: "Multiracial",
  unknown: "Unknown",
};

function pct(num, denom) {
  if (!denom) return 0;
  return num / denom;
}

function fmtPct(p) {
  return `${(p * 100).toFixed(1)}%`;
}

function thresholdRow(label, raceKey, count, total, threshold) {
  const suppressed = count < SUPPRESS_N;
  const share = suppressed ? 0 : pct(count, total);
  const meets = !suppressed && share >= threshold;
  const progressFill = suppressed ? 0 : Math.min(100, (share / threshold) * 100);
  return { label, raceKey, count, share, threshold, meets, progressFill, suppressed };
}

function exportDAPReport(trial, fdaRows, assessedRows, suppressedCount) {
  const today = new Date().toISOString().slice(0, 10);
  const header = ["Group", "Enrolled", "Share (%)", "Target (%)", "Meets threshold", "Suppressed"];
  const rows = fdaRows.map((r) => [
    r.label,
    r.suppressed ? "<5" : r.count,
    r.suppressed ? "suppressed" : (r.share * 100).toFixed(1),
    (r.threshold * 100).toFixed(1),
    r.suppressed ? "N/A" : (r.meets ? "Yes" : "No"),
    r.suppressed ? "Yes" : "No",
  ]);
  const metaRows = [
    ["GTH DAP Progress Report"],
    [`Trial: ${trial.name}`],
    [`Trial ID: ${trial.id}`],
    [`Export date: ${today}`],
    [`Enrolled: ${trial.enrolled} / ${trial.target}`],
    [`Assessed groups: ${assessedRows.length} of ${fdaRows.length} (${suppressedCount} suppressed)`],
    [],
  ];
  const csvRows = [
    ...metaRows.map((r) => r.map((v) => `"${v ?? ""}"`).join(",")),
    header.map((v) => `"${v}"`).join(","),
    ...rows.map((r) => r.map((v) => `"${v}"`).join(",")),
  ];
  const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `GTH-DAP-${trial.id}-${today}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  track("dap_export", { trialId: trial.id });
}

export function EquityDashboard() {
  const sectionRef = useFeatureView("equity_dashboard");
  const [trialId, setTrialId] = useState(TRIALS[0].id);
  const trial = TRIALS.find((t) => t.id === trialId) ?? TRIALS[0];

  const totalRace = useMemo(() => {
    return Object.values(trial.cohort.race).reduce((s, n) => s + n, 0);
  }, [trial]);

  const totalEthnicity = useMemo(() => {
    return Object.values(trial.cohort.ethnicity).reduce((s, n) => s + n, 0);
  }, [trial]);

  const fdaRows = useMemo(() => {
    return [
      thresholdRow("Black or African American", "black", trial.cohort.race.black, totalRace, trial.thresholds.black),
      thresholdRow("Hispanic or Latino", "hispanic", trial.cohort.ethnicity.hispanic, totalEthnicity, trial.thresholds.hispanic),
      thresholdRow("Asian", "asian", trial.cohort.race.asian, totalRace, trial.thresholds.asian),
      thresholdRow("American Indian / AN", "aian", trial.cohort.race.aian, totalRace, trial.thresholds.aian),
    ];
  }, [trial, totalRace, totalEthnicity]);

  const assessedRows = fdaRows.filter((r) => !r.suppressed);
  const suppressedCount = fdaRows.filter((r) => r.suppressed).length;
  const meetsAll = assessedRows.length > 0 && assessedRows.every((r) => r.meets);
  const enrollmentPct = trial.enrolled / trial.target;

  return (
    <section ref={sectionRef} className="ds-section feature-section" id="feature-equity-dashboard">
      <div className="ds-page">
        <div className="ds-section-head">
          <div className="ds-section-num">05 / 05</div>
          <div>
            <h2 className="ds-section-title">Equity analytics</h2>
            <p className="ds-section-sub">
              Real-time diversity tracking against FDA Diversity Action Plan
              thresholds — sponsors see compliance posture as the cohort
              enrolls, not after the fact.
            </p>
          </div>
        </div>

        <div className="feature-pill-row">
          {TRIALS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`feature-pill${t.id === trialId ? " feature-pill--active" : ""}`}
              onClick={() => { setTrialId(t.id); track("equity_trial_switch", { trialId: t.id }); }}
            >
              {t.name} <span className="s">/</span> {t.enrolled}/{t.target}
            </button>
          ))}
        </div>

        {/* Z-pattern: executive summary row — enrollment + FDA posture only */}
        <div className="ds-grid-2 feature-spaced">
          <div className="ds-card eq-kpi-card">
            <div className="ds-card-label">
              <span>ENROLLMENT</span>
              <span>{fmtPct(enrollmentPct)} OF TARGET</span>
            </div>
            <div className="feature-stat-num">{trial.enrolled} / {trial.target}</div>
            <div className="feature-domain-bar feature-spaced">
              <div className="feature-domain-fill" style={{ width: `${enrollmentPct * 100}%` }} />
            </div>
            <p className="feature-help">Target: {trial.target} participants · {trial.name}</p>
          </div>

          <div className="ds-card eq-kpi-card">
            <div className="ds-card-label">
              <span>FDA POSTURE</span>
              <button
                type="button"
                className="eq-export-btn"
                onClick={() => exportDAPReport(trial, fdaRows, assessedRows, suppressedCount)}
                title="Download DAP progress report as CSV"
              >
                ↓ Export DAP report
              </button>
            </div>
            <div className="feature-tier">
              <Badge status={meetsAll ? "live" : "flag"}>
                {meetsAll ? "On track" : "At risk"}
              </Badge>
            </div>
            <p className="feature-help">
              {suppressedCount > 0 && (
                <span>{suppressedCount} group(s) suppressed (n&lt;{SUPPRESS_N}) · not assessed. </span>
              )}
              {meetsAll
                ? `${assessedRows.length} assessed group(s) meet diversity targets.`
                : `${assessedRows.filter((r) => !r.meets).length} assessed group(s) below threshold. Sponsor outreach recommended.`}
            </p>
          </div>
        </div>

        <div className="ds-card feature-spaced">
          <div className="ds-card-label">
            <span>FDA THRESHOLDS</span>
            <span>{assessedRows.filter((r) => r.meets).length} / {assessedRows.length} ASSESSED ON TRACK{suppressedCount > 0 ? ` · ${suppressedCount} SUPPRESSED` : ""}</span>
          </div>
          <table className="ds-spec-table">
            <thead>
              <tr>
                <th>Group</th>
                <th>Enrolled</th>
                <th>Share</th>
                <th>Target</th>
                <th>Progress</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {fdaRows.map((r) => (
                <tr key={r.raceKey}>
                  <td>{r.label}</td>
                  <td>
                    {r.suppressed
                      ? <span className="eq-suppressed">&lt;{SUPPRESS_N}</span>
                      : r.count}
                  </td>
                  <td>
                    {r.suppressed
                      ? <span className="eq-suppressed">—</span>
                      : fmtPct(r.share)}
                  </td>
                  <td>{fmtPct(r.threshold)}</td>
                  <td style={{ width: 220 }}>
                    {r.suppressed
                      ? <span className="eq-suppressed">suppressed · n&lt;{SUPPRESS_N}</span>
                      : (
                        <div className="feature-domain-bar">
                          <div className="feature-domain-fill" style={{ width: `${r.progressFill}%` }} />
                        </div>
                      )}
                  </td>
                  <td>
                    {r.suppressed
                      ? <Tag variant="default">suppressed</Tag>
                      : <Tag variant={r.meets ? "slash" : "default"}>{r.meets ? "/ on track" : "below"}</Tag>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {suppressedCount > 0 && (
            <div className="eq-suppress-note">
              Cells marked &lt;{SUPPRESS_N} are suppressed to prevent re-identification risk at small cohort sizes.
              Suppressed groups are excluded from DAP compliance assessment.
              In production, apply OMB cell suppression rules consistently across site-level and aggregate reports.
            </div>
          )}
        </div>

        {/* Supporting detail — 4-panel demographic breakdown */}
        <div className="ds-card-label feature-spaced" style={{ paddingBottom: 8 }}>
          <span>DEMOGRAPHIC DETAIL</span>
          <span>SUPPORTING DATA</span>
        </div>
        <div className="ds-grid-2">
          <div className="ds-card">
            <div className="ds-card-label">
              <span>RACE</span>
              <span>{totalRace} REPORTED</span>
            </div>
            <div className="feature-domain-list">
              {Object.entries(trial.cohort.race).map(([k, v]) => (
                <div key={k} className="feature-domain-row">
                  <span className="feature-domain-name">{RACE_LABEL[k] ?? k}</span>
                  <div className="feature-domain-bar">
                    <div
                      className="feature-domain-fill"
                      style={{ width: v < SUPPRESS_N ? 0 : `${(v / totalRace) * 100}%` }}
                    />
                  </div>
                  <span className="feature-domain-score">
                    {v < SUPPRESS_N
                      ? <span className="eq-suppressed">&lt;{SUPPRESS_N}</span>
                      : v}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="ds-card">
            <div className="ds-card-label">
              <span>SEX DISTRIBUTION</span>
              <span>SELF-REPORTED</span>
            </div>
            <div className="feature-domain-list">
              {Object.entries(trial.cohort.sex).map(([k, v]) => {
                const total = Object.values(trial.cohort.sex).reduce((s, n) => s + n, 0);
                return (
                  <div key={k} className="feature-domain-row">
                    <span className="feature-domain-name">{k}</span>
                    <div className="feature-domain-bar">
                      <div className="feature-domain-fill" style={{ width: `${(v / total) * 100}%` }} />
                    </div>
                    <span className="feature-domain-score">{fmtPct(pct(v, total))}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="ds-card">
            <div className="ds-card-label">
              <span>AGE BANDS</span>
              <span>YEARS</span>
            </div>
            <div className="feature-domain-list">
              {Object.entries(trial.cohort.ageBands).map(([k, v]) => {
                const total = Object.values(trial.cohort.ageBands).reduce((s, n) => s + n, 0);
                return (
                  <div key={k} className="feature-domain-row">
                    <span className="feature-domain-name">{k}</span>
                    <div className="feature-domain-bar">
                      <div className="feature-domain-fill" style={{ width: `${(v / total) * 100}%` }} />
                    </div>
                    <span className="feature-domain-score">{v}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="ds-card">
            <div className="ds-card-label">
              <span>URBANICITY</span>
              <span>RUCA-DERIVED</span>
            </div>
            <div className="feature-domain-list">
              {Object.entries(trial.cohort.urbanicity).map(([k, v]) => {
                const total = Object.values(trial.cohort.urbanicity).reduce((s, n) => s + n, 0);
                return (
                  <div key={k} className="feature-domain-row">
                    <span className="feature-domain-name">{k}</span>
                    <div className="feature-domain-bar">
                      <div className="feature-domain-fill" style={{ width: `${(v / total) * 100}%` }} />
                    </div>
                    <span className="feature-domain-score">{v}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default EquityDashboard;
